<template>
    <div class="at-rc-wrapper">
        <div class="filters-panel no-print">
            <div class="filters-wrapper">
                <div class="filterby-wrapper">
                    <SelectBox
                        v-model="actionsFilter"
                        :placeholder="$t('auditTrail.filterBy.filterBy')"
                        :options="actionsFilterOptions"
                    />
                </div>

                <div class="date-range-wrapper">
                    <div class="date-range-type-wrapper">
                        <SelectBox
                            v-model="dateRange.type"
                            style-class="date-range-type-select-box audit-trail-show-on-narrow-screen"
                            :placeholder="$t('auditTrail.period')"
                            :options="dateRange.typeOptions"
                        />
                        <button
                            :class="[
                                'btn',
                                'time-range-button',
                                'audit-trail-show-on-wide-screen',
                                {
                                    active: dateRange.type === DATE_RANGE.DAY,
                                },
                            ]"
                            @click="dateRange.type = DATE_RANGE.DAY"
                        >
                            {{ $t('auditTrail.dateRange.oneDay') }}
                        </button>
                        <button
                            :class="[
                                'btn',
                                'time-range-button',
                                'audit-trail-show-on-wide-screen',
                                {
                                    active: dateRange.type === DATE_RANGE.WEEK,
                                },
                            ]"
                            @click="dateRange.type = DATE_RANGE.WEEK"
                        >
                            {{ $t('auditTrail.dateRange.nDays', { n: DATE_RANGE.WEEK }) }}
                        </button>
                        <button
                            :class="[
                                'btn',
                                'time-range-button',
                                'audit-trail-show-on-wide-screen',
                                {
                                    active: dateRange.type === DATE_RANGE.MONTH,
                                },
                            ]"
                            @click="dateRange.type = DATE_RANGE.MONTH"
                        >
                            {{ $t('auditTrail.dateRange.nDays', { n: DATE_RANGE.MONTH }) }}
                        </button>
                        <button
                            :class="[
                                'btn',
                                'time-range-button',
                                'audit-trail-show-on-wide-screen',
                                {
                                    active: dateRange.type === DATE_RANGE.YEAR,
                                },
                            ]"
                            @click="dateRange.type = DATE_RANGE.YEAR"
                        >
                            {{ $t('auditTrail.dateRange.nDays', { n: DATE_RANGE.YEAR }) }}
                        </button>
                        <div class="timepickers-wrapper">
                            <DateTimePickerForm ref="dateRangeForm" v-model="dateRange" />
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="totalRecords > RECORDS_LIMIT" class="at-rc-stats">
                <span v-if="!isLoading" v-html="getCurrentRecordsRange()">
                    From
                    <span class="numeric">{{ offset }}</span> to
                    <span class="numeric">{{ roomConfiguration.length + offset }}</span> records are loaded
                    from the <span class="numeric">{{ totalRecords }}</span> in total.
                </span>
                <button :class="['btn-previous', { disabled: offset === 0 }]" @click="showLess">
                    <i class="icon-chewron-down"></i>
                    {{ $t('auditTrail.roomConf.filter.prevBtn') }}
                </button>
                <button :class="['btn-next', { disabled: !moreRecords }]" @click="showMore">
                    {{ $t('auditTrail.roomConf.filter.nextBtn') }}
                    <i class="icon-chewron-down"></i>
                </button>
            </div>
        </div>
        <div class="at-rc-data-wrapper no-print">
            <div v-if="displayedHistory.length === 0" v-show="isLoaded" class="data-empty">
                <h3 v-text="$t('auditTrail.roomConf.noHistory')"></h3>
            </div>
            <div v-else v-show="isLoaded" class="at-rc-table">
                <div class="at-rc-table__row at-rc-table__row_header">
                    <div class="at-rc-table__cell">
                        {{ $t('auditTrail.roomConf.table.header.username') }}
                    </div>
                    <div class="at-rc-table__cell">
                        {{ $t('auditTrail.roomConf.table.header.action') }}
                    </div>
                    <div class="at-rc-table__cell">
                        {{ $t('auditTrail.roomConf.table.header.location') }}
                    </div>
                    <div class="at-rc-table__cell">
                        {{ $t('auditTrail.roomConf.table.header.device') }}
                    </div>
                    <div class="at-rc-table__cell">
                        {{ $t('auditTrail.roomConf.table.header.datetime') }}
                    </div>
                </div>

                <div
                    v-for="item in displayedHistory"
                    :key="item.room_conf_id"
                    class="at-rc-table__row at-rc-table__row_body"
                >
                    <div class="at-rc-table__cell" :title="item.first_name">
                        {{ item.first_name }}
                    </div>
                    <div
                        class="at-rc-table__cell at-rc-table__cell_actions"
                        v-html="getActionLabels(item.actions)"
                    ></div>
                    <div
                        class="at-rc-table__cell at-rc-table__cell_location-paths"
                        v-html="getLocationName(item.location_id)"
                    ></div>
                    <div class="at-rc-table__cell" :title="getModuleName(item.peripheral_id, item.actions)">
                        {{ getModuleName(item.peripheral_id, item.actions) }}
                    </div>
                    <div class="at-rc-table__cell">
                        {{ getTime(item.time) }}
                    </div>
                </div>
            </div>
            <Pagination
                v-show="pagination.last_page > 1"
                v-model="pagination.current_page"
                :options="pagination"
                :callback="() => {}"
            />
        </div>
        <div class="print-only">
            <div class="filters-panel">
                <table>
                    <tr>
                        <td>{{ $t('auditTrail.filterBy.filterBy') }}</td>
                        <td>{{ filterByAction() }}</td>
                    </tr>
                    <tr>
                        <td>{{ $t('auditTrail.searchBy') }}</td>
                        <td>{{ searchKeyword && filteredHistory.length > 0 ? searchByKeyword : '-' }}</td>
                    </tr>
                    <tr>
                        <td>{{ $t('auditTrail.period') }}</td>
                        <td>{{ dateRangePeriodPrinted }}</td>
                    </tr>
                    <tr v-if="totalRecords > RECORDS_LIMIT">
                        <td>{{ $t('auditTrail.roomConf.filter.printedRangeTitle') }}</td>
                        <td v-html="getCurrentRecordsRange()"></td>
                    </tr>
                </table>
            </div>
            <h4
                v-if="searchKeyword && filteredHistory.length > 0"
                v-text="$t('print.search', { val: searchKeyword })"
            ></h4>
            <table
                v-if="roomConfiguration.length > 0"
                v-show="isLoaded"
                id="forAT_table-AuditTraail-RoomConf-print"
                class="table print-room-history"
            >
                <thead>
                    <tr class="tbl-header">
                        <th class="username">{{ $t('auditTrail.roomConf.table.header.username') }}</th>
                        <th class="action">{{ $t('auditTrail.roomConf.table.header.action') }}</th>
                        <th class="location">{{ $t('auditTrail.roomConf.table.header.location') }}</th>
                        <th class="device">{{ $t('auditTrail.roomConf.table.header.device') }}</th>
                        <th class="datetime">{{ $t('auditTrail.roomConf.table.header.datetime') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="item in filteredHistory.length === 0 ? roomConfiguration : filteredHistory"
                        :key="item.room_conf_id"
                    >
                        <td class="userName" v-text="item.first_name"></td>
                        <td class="action"><div v-html="getActionLabels(item.actions)"></div></td>
                        <td class="location" v-html="getLocationName(item.location_id)"></td>
                        <td class="device" v-text="getModuleName(item.peripheral_id, item.actions)"></td>
                        <td class="datetime" v-text="getTime(item.time)"></td>
                    </tr>
                </tbody>
            </table>
            <h3 v-else class="data-empty" v-text="$t('auditTrail.roomConf.noHistory')"></h3>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapActions, mapState } from 'vuex';
import format from 'date-fns/format';

import { resourceGetPeripherals, resourceRoomConfHistory } from '../../api/resources';

import { getCancelToken } from '../../api';

import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import mappingState from '../common/mappingState';
import { getFullDateFormat, ifIsNotCanceled, getDateFormat } from '../common/helpers';

import isSameDay from 'date-fns/isSameDay';
import isToday from 'date-fns/isToday';
import set from 'date-fns/set';
import subDays from 'date-fns/subDays';

import { AUDIT_TRAIL__ROOM_CONF_NAVIGATION_NUMBER, AUDIT_TRAIL, DEF_DATE_FORMAT } from '../../constants';
import DateTimePickerForm from '../common/datetimepicker/DateTimePickerForm.vue';
import SelectBox from '../common/SelectBox';
import isEqual from 'date-fns/isEqual';

const DATE_RANGE_TYPES = Object.values(AUDIT_TRAIL.DATE_RANGE);
export default {
    name: 'RoomConfiguration',

    components: {
        Loader,
        Pagination,
        SelectBox,
        DateTimePickerForm,
    },

    mixins: [mappingState],

    constants: {
        DATE_RANGE: AUDIT_TRAIL.DATE_RANGE,
        RECORDS_LIMIT: AUDIT_TRAIL.RECORDS_LIMIT,
    },

    data() {
        return {
            isLoaded: false,
            modules: [],
            roomConfiguration: [],
            cancelToken: getCancelToken(),
            pagination: {
                current_page: 1,
                last_page: 0,
                per_page: AUDIT_TRAIL__ROOM_CONF_NAVIGATION_NUMBER,
            },
            offset: 0,
            moreRecords: false,
            totalRecords: 0,
            actionsFilter: 'All',
            actionsFilterOptions: [
                { value: 'All', name: this.$t('auditTrail.roomConf.filter.allActions') },
                { value: ['enabled'], name: this.$t('auditTrail.roomConf.filter.enabled') },
                { value: ['upgrade'], name: this.$t('auditTrail.roomConf.filter.upgrade') },
                // { value: ['listen_in'], name: this.$t('auditTrail.roomConf.filter.listenIn') },
                { value: ['acoustic'], name: this.$t('auditTrail.roomConf.filter.acoustic') },
                {
                    value: ['duration', 'threshold'],
                    name: this.$t('auditTrail.roomConf.filter.acousticSettings'),
                },
                { value: ['cancelAcoustic'], name: this.$t('auditTrail.roomConf.filter.cancelAcoustic') },
            ],
            dateRange: {
                type: AUDIT_TRAIL.DATE_RANGE.WEEK,
                typeOptions: [
                    {
                        value: AUDIT_TRAIL.DATE_RANGE.DAY,
                        name: this.$t('auditTrail.dateRange.oneDay'),
                    },
                    {
                        value: AUDIT_TRAIL.DATE_RANGE.WEEK,
                        name: this.$t('auditTrail.dateRange.nDays', { n: AUDIT_TRAIL.DATE_RANGE.WEEK }),
                    },
                    {
                        value: AUDIT_TRAIL.DATE_RANGE.MONTH,
                        name: this.$t('auditTrail.dateRange.nDays', { n: AUDIT_TRAIL.DATE_RANGE.MONTH }),
                    },
                    {
                        value: AUDIT_TRAIL.DATE_RANGE.YEAR,
                        name: this.$t('auditTrail.dateRange.nDays', { n: AUDIT_TRAIL.DATE_RANGE.YEAR }),
                    },
                ],
                from: {
                    dateSaved: null,
                    date: set(subDays(new Date(), AUDIT_TRAIL.DATE_RANGE.WEEK - 1), {
                        hours: 0,
                        minutes: 0,
                        seconds: 0,
                        milliseconds: 0,
                    }),
                },
                to: {
                    dateSaved: null,
                    date: set(new Date(), { hours: 23, minutes: 59, seconds: 0, milliseconds: 0 }),
                },
                shortFormat: true,
            },
            auditTrailData: [],
        };
    },

    computed: {
        ...mapState({
            allLocations: state => state.allLocations,
            isLoading: state => state.isLoading,
        }),

        /**
         * Check if date range is valid
         */
        dateRangeFormValid() {
            return (
                !this.$refs.dateRangeForm.$v.dateFromFormatted.$invalid &&
                !this.$refs.dateRangeForm.$v.dateToFormatted.$invalid
            );
        },

        /**
         * Get selected date range for print version
         */
        dateRangePeriodPrinted() {
            return this.dateRange.from.dateSaved && this.dateRange.to.dateSaved
                ? this.$t('auditTrail.selectedTime', {
                      from: format(this.dateRange.from.dateSaved, getDateFormat()),
                      to: format(this.dateRange.to.dateSaved, getDateFormat()),
                  })
                : this.dateRange.from.dateSaved
                ? this.$t('auditTrail.startTime', {
                      from: format(this.dateRange.from.dateSaved, getDateFormat()),
                  })
                : this.dateRange.to.dateSaved
                ? this.$t('auditTrail.endTime', {
                      to: format(this.dateRange.to.dateSaved, getDateFormat()),
                  })
                : this.$t('auditTrail.fullPeriod');
        },

        locationPaths() {
            return this.roomConfiguration.reduce((paths, row) => {
                let location = this.allLocations.find(location => location.id === row.location_id);

                if (location) {
                    let locationPath = location.path.concat(location.name);

                    return Object.assign(paths, { [location.id]: locationPath });
                }

                return paths;
            }, {});
        },

        locationPathLabels() {
            return Object.keys(this.locationPaths).reduce((labels, locationId) => {
                let locationPath = this.locationPaths[locationId]
                    .map(name => `<span title=\"${name}\"> ${name} / </span>`)
                    .join('')
                    .replace(/\s\/\s<\/span>$/, '</span>');

                return Object.assign(labels, { [locationId]: locationPath });
            }, {});
        },

        moduleNames() {
            return this.modules.reduce((moduleNames, module) => {
                return Object.assign(moduleNames, { [module.peripheral_id]: module.name });
            }, {});
        },

        paginationLastPage() {
            return Math.ceil(this.filteredHistory.length / AUDIT_TRAIL__ROOM_CONF_NAVIGATION_NUMBER);
        },

        filteredHistory() {
            let keyword = this.searchKeyword.toLowerCase().trim();

            if (keyword === '') {
                return this.roomConfiguration;
            }

            return this.roomConfiguration.filter(item => {
                let staffName = item.first_name.toLowerCase().trim();
                let moduleName = (this.moduleNames[item.peripheral_id] || '').toLowerCase().trim();
                let locations = this.locationPaths[item.location_id]
                    ? this.locationPaths[item.location_id]
                    : [];

                let isStaffName = staffName.includes(keyword);
                let isModuleName = !!moduleName && moduleName.includes(keyword);
                let isLocationName = locations.some(location => {
                    return location
                        .toLowerCase()
                        .trim()
                        .includes(keyword);
                });

                return isStaffName || isModuleName || isLocationName;
            });
        },

        displayedHistory() {
            let currentPage = this.pagination.current_page;

            let start = Math.max((currentPage - 1) * AUDIT_TRAIL__ROOM_CONF_NAVIGATION_NUMBER, 0);
            let end = currentPage * AUDIT_TRAIL__ROOM_CONF_NAVIGATION_NUMBER;

            return this.filteredHistory.slice(start, end);
        },

        /**
         * Keyword of search for print
         */
        searchByKeyword() {
            return this.searchKeyword.length > 0 ? this.searchKeyword : '-';
        },
    },

    watch: {
        searchKeyword() {
            this.pagination.current_page = 1;
        },

        paginationLastPage(val) {
            this.pagination.last_page = val;
        },

        actionsFilter() {
            this.$nextTick(() => {
                this.loadHistoryWithLoader();
            });
        },

        'dateRange.from.date'() {
            this.$nextTick(() => {
                // Wait for latest validation update in DateTimePickerForm subcomponent
                if (this.dateRangeFormValid) {
                    this.loadHistoryWithLoader();
                    this.setDateRangeTypeByDates();
                }
            });
        },
        'dateRange.to.date'() {
            this.$nextTick(() => {
                // Wait for latest validation update in DateTimePickerForm subcomponent
                // Check for loader to avoid multiple history requests in case if the 'from' date already changed.
                if (this.dateRangeFormValid & !this.isLoading) {
                    this.loadHistoryWithLoader();
                    this.setDateRangeTypeByDates();
                }
            });
        },
        'dateRange.type'(dateRangeType) {
            if (DATE_RANGE_TYPES.includes(dateRangeType)) {
                this.setDateRangeType(dateRangeType);
            }
        },
    },

    created() {
        this.showLoading(true);

        Promise.all([this.loadModules(), this.loadHistory()])
            .then(data => {
                let modules = data[0];
                let history = data[1];

                this.modules = modules;
                this.roomConfiguration = history ? history : [];
                this.setSearchEnabled(this.roomConfiguration.length > 0);

                const lastPage = Math.ceil(history.length / AUDIT_TRAIL__ROOM_CONF_NAVIGATION_NUMBER);
                this.pagination.last_page = lastPage;

                this.showLoading(false);
                this.isLoaded = true;
            })
            .catch(
                ifIsNotCanceled({
                    callback: () => {
                        this.setSearchEnabled(this.roomConfiguration.length > 0);
                        this.showLoading(false);
                        this.isLoaded = true;
                    },
                })
            );
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapActions({
            popup: 'popup/popup',
        }),

        getTime(time) {
            return format(new Date(time), getFullDateFormat());
        },

        loadModules() {
            return resourceGetPeripherals({
                cancelToken: this.cancelToken.token,
            }).then(({ data }) => {
                return [].concat(data);
            });
        },

        loadHistory() {
            return resourceRoomConfHistory({
                cancelToken: this.cancelToken.token,
                params: {
                    time_from: this.dateRange.from.date
                        ? format(
                              set(this.dateRange.from.date, {
                                  hours: 0,
                                  minutes: 0,
                                  seconds: 0,
                                  milliseconds: 0,
                              }),
                              DEF_DATE_FORMAT
                          )
                        : null,
                    time_to: this.dateRange.to.date
                        ? format(
                              set(this.dateRange.to.date, {
                                  hours: 23,
                                  minutes: 59,
                                  seconds: 0,
                                  milliseconds: 0,
                              }),
                              DEF_DATE_FORMAT
                          )
                        : null,
                    actions_filter: this.actionsFilter === 'All' ? null : this.actionsFilter,
                    offset: this.offset,
                },
            }).then(({ data }) => {
                this.totalRecords = data.rows;
                this.moreRecords = data.rows > data.events.length + this.offset;
                this.saveLastCorrectDateRange();
                return data.events;
            });
        },

        loadHistoryWithLoader() {
            this.showLoading(true);
            return this.loadHistory()
                .then(response => {
                    this.roomConfiguration = response ? response : [];
                    this.setSearchEnabled(this.roomConfiguration.length > 0);

                    const lastPage = Math.ceil(
                        this.filteredHistory.length / AUDIT_TRAIL__ROOM_CONF_NAVIGATION_NUMBER
                    );
                    this.pagination.last_page = lastPage;
                    this.pagination.current_page = 1;
                })
                .then(() => {
                    this.showLoading(false);
                })
                .catch(response => {
                    ifIsNotCanceled(response);
                });
        },

        getActionLabels(actions) {
            let labels = [];
            if (actions.cancelAcoustic === true) {
                labels.push(this.$t('auditTrail.roomConf.cancelAcousticCalls'));
            }

            if (actions.hasOwnProperty('enabled')) {
                let label = actions.enabled
                    ? this.$t('auditTrail.roomConf.enableCalls')
                    : this.$t('auditTrail.roomConf.disableCalls');

                labels.push(label);
            }

            if (actions.hasOwnProperty('upgrade')) {
                let label = actions.upgrade
                    ? this.$t('auditTrail.roomConf.upgradeCalls')
                    : this.$t('auditTrail.roomConf.downgradeCalls');

                labels.push(label);
            }

            if (actions.hasOwnProperty('listen_in')) {
                let label = actions.listen_in
                    ? this.$t('auditTrail.roomConf.enableListenIn')
                    : this.$t('auditTrail.roomConf.disableListenIn');

                labels.push(label);
            }

            if (actions.hasOwnProperty('acoustic')) {
                let label = actions.acoustic
                    ? this.$t('auditTrail.roomConf.enableAcoustic')
                    : this.$t('auditTrail.roomConf.disableAcoustic');

                labels.push(label);
            }

            if (actions.hasOwnProperty('threshold')) {
                labels.push(this.$t('auditTrail.roomConf.acousticCallThreshold', { val: actions.threshold }));
            }

            if (actions.hasOwnProperty('duration')) {
                labels.push(this.$t('auditTrail.roomConf.acousticCallDuration', { val: actions.duration }));
            }

            return labels.map(label => `<span title=\"${label}\">${label}</span>`).join('');
        },

        getCurrentRecordsRange() {
            return this.$t('auditTrail.roomConf.filter.recordsRange', {
                from: '<span class="numeric">' + this.offset + '</span>',
                to: '<span class="numeric">' + (this.roomConfiguration.length + this.offset) + '</span>',
                total: '<span class="numeric">' + this.totalRecords + '</span>',
            });
        },

        getLocationName(id) {
            return this.locationPathLabels[id]
                ? this.locationPathLabels[id]
                : '<span>' + this.$t('auditTrail.table.content.unknown') + '</span>';
        },

        getModuleName(modId, actions) {
            return this.moduleNames[modId] || actions.cancelAcoustic
                ? this.moduleNames[modId]
                : this.$t('auditTrail.table.content.unknown');
        },

        /**
         * Set date range type using buttons
         * @param {Number} dateRangeType
         */
        setDateRangeType(dateRangeType) {
            this.dateRange.type = dateRangeType;
            let today = new Date();
            this.dateRange.from.date = set(subDays(today, dateRangeType - 1), {
                hours: 0,
                minutes: 0,
                seconds: 0,
                milliseconds: 0,
            });
            if (
                !isEqual(
                    this.dateRange.to.date,
                    set(today, { hours: 23, minutes: 59, seconds: 0, milliseconds: 0 })
                )
            ) {
                this.dateRange.to.date = set(today, {
                    hours: 23,
                    minutes: 59,
                    seconds: 0,
                    milliseconds: 0,
                });
            }
        },

        /**
         * Set date range type using set dates
         */
        setDateRangeTypeByDates() {
            let today = new Date();
            this.dateRange.type = null;

            if (isToday(this.dateRange.to.date)) {
                DATE_RANGE_TYPES.forEach(dateRangeType => {
                    let calculatedDate = subDays(today, dateRangeType - 1);
                    if (isSameDay(this.dateRange.from.date, calculatedDate)) {
                        this.dateRange.type = dateRangeType;
                    }
                });
            }
            this.$refs.dateRangeForm.updateDateFields();
        },

        showMore() {
            if (this.totalRecords > this.offset + AUDIT_TRAIL.RECORDS_LIMIT) {
                this.offset += AUDIT_TRAIL.RECORDS_LIMIT;
                this.loadHistoryWithLoader();
            }
        },

        showLess() {
            if (this.offset > 0) {
                this.offset -= AUDIT_TRAIL.RECORDS_LIMIT;
                this.loadHistoryWithLoader();
            }
        },

        filterByAction() {
            return this.actionsFilter === 'All'
                ? this.$t('auditTrail.roomConf.filter.allActions')
                : this.actionsFilterOptions.find(item => item.value === this.actionsFilter).name;
        },

        /**
         * Save last correct date range
         */
        saveLastCorrectDateRange() {
            this.dateRange.from.dateSaved = this.dateRange.from.date || null;
            this.dateRange.to.dateSaved = this.dateRange.to.date || null;
        },
    },
};
</script>
